import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    createAppliedRemoteGardenEnvelope,
    createNextGardenEnvelope,
    getRemoteGardenDecision,
    resolveGardenMerge,
} from '../../domain/garden/gardenEnvelope';
import { useAuth } from '../account/AuthProvider';
import {
    GARDEN_SETTING_KEY,
    createSeededAccountEnvelope,
    parseGarden,
    readAccountEnvelope,
    readDeviceId,
    readGarden,
    writeAccountEnvelope,
    writeGuestGarden,
} from './gardenStore';

// Storage moved to `gardenStore` (SPEC-022); the names stay importable from
// here because this hook has been their home since SPEC-014.
export { GARDEN_SETTING_KEY, parseGarden, readGarden };

/**
 * My Garden, as a list of plant ids (SPEC-003 D-6, SPEC-014, SPEC-022).
 *
 * Local first, exactly as it has been since SPEC-014: every change lands in
 * storage synchronously and the whole feature works signed out and offline.
 * What SPEC-022 adds is an account layer behind the same seam — signed in, the
 * garden lives in a revision-versioned envelope that syncs to
 * `plant/gardens/users/{uid}` through habb.me's compare-and-set mechanism, so
 * it survives the evictions and reinstalls that lose browser storage.
 *
 * ONE provider owns the engine. Four components call `useGarden()`, and four
 * independent sync loops would race each other over one document — the same
 * reason habb.me runs a single `useCloudSync` in its shell.
 *
 * The heart in the hero and "Add to my garden" at the foot are two affordances
 * over this ONE list. A second "favourites" collection would have no screen to
 * live on and nothing to distinguish it from the garden.
 */

const SAVE_RETRY_DELAY_MS = 3000;

function loadGardenSyncClient() {
    return import('../../infrastructure/cloud/gardenSyncClient');
}

const GardenContext = createContext(null);

export function GardenProvider({ children }) {
    const { user, isAuthenticated } = useAuth();
    const uid = isAuthenticated && user ? user.uid : null;

    // Guest sessions carry a plain list; account sessions carry the envelope
    // the list is derived from. `session.uid` lags `uid` by one effect run —
    // reads and writes key off `session.uid`, so a bucket is never written
    // with another bucket's content mid-switch.
    const [session, setSession] = useState(() => ({ uid: null, plantIds: readGarden(), envelope: null }));
    const [hasHydratedRemote, setHasHydratedRemote] = useState(false);
    const [remoteTick, setRemoteTick] = useState(0);
    const [saveTick, setSaveTick] = useState(0);
    const deviceIdRef = useRef(null);
    const isSavingRef = useRef(false);

    if (deviceIdRef.current === null) {
        deviceIdRef.current = readDeviceId();
    }

    const deviceId = deviceIdRef.current;

    // Bucket switching: sign-in hydrates (or seeds) the account bucket,
    // sign-out returns to the guest list untouched.
    useEffect(() => {
        setHasHydratedRemote(false);

        if (!uid) {
            setSession({ uid: null, plantIds: readGarden(), envelope: null });
            return;
        }

        const now = new Date().toISOString();
        const envelope = readAccountEnvelope(uid, { deviceId, now })
            || createSeededAccountEnvelope({ deviceId, now });

        writeAccountEnvelope(uid, envelope);
        setSession({ uid, plantIds: envelope.garden.plantIds, envelope });
    }, [uid, deviceId]);

    // The one-shot remote read, re-run on every refresh tick. What it finds is
    // answered by the domain's four-way decision; a genuine both-sides-moved
    // resolves by union with nobody asked (SPEC-022 D-2).
    useEffect(() => {
        if (!uid || session.uid !== uid || !session.envelope) {
            return undefined;
        }

        let cancelled = false;

        loadGardenSyncClient()
            .then(({ loadGardenCloudState }) => loadGardenCloudState(uid))
            .then((remoteEnvelope) => {
                if (cancelled) {
                    return;
                }

                setSession((current) => {
                    if (current.uid !== uid || !current.envelope) {
                        return current;
                    }

                    const now = new Date().toISOString();
                    const decision = getRemoteGardenDecision(current.envelope, remoteEnvelope);

                    if (decision === 'take-remote') {
                        const applied = createAppliedRemoteGardenEnvelope(remoteEnvelope, { deviceId, now });

                        writeAccountEnvelope(uid, applied);
                        return { uid, plantIds: applied.garden.plantIds, envelope: applied };
                    }

                    if (decision === 'merge') {
                        const resolved = resolveGardenMerge(current.envelope, remoteEnvelope, { deviceId, now });

                        writeAccountEnvelope(uid, resolved);
                        return { uid, plantIds: resolved.garden.plantIds, envelope: resolved };
                    }

                    return current;
                });
                setHasHydratedRemote(true);
            })
            .catch((error) => {
                // Offline is the common case, not an error state: the cached
                // bucket is already on screen, and the next focus or reconnect
                // retries the read. A never-hydrated session also never saves,
                // which is what protects the cloud copy from a device that
                // cannot see it.
                console.error(error);
            });

        return () => {
            cancelled = true;
        };
    }, [uid, session.uid, Boolean(session.envelope), remoteTick, deviceId]);

    // The save effect: pendingSync is the whole queue. It only runs after the
    // remote read has answered once — pushing before knowing the remote state
    // would turn every cold start into a stale-write round trip.
    useEffect(() => {
        const envelope = session.uid === uid ? session.envelope : null;

        if (!uid || !envelope || !envelope.syncMeta.pendingSync || !hasHydratedRemote || isSavingRef.current) {
            return undefined;
        }

        let retryTimer = null;
        let cancelled = false;

        isSavingRef.current = true;

        loadGardenSyncClient()
            .then(({ saveGardenCloudState, GardenSyncStaleWriteError }) => saveGardenCloudState(uid, envelope)
                .then((result) => {
                    if (cancelled) {
                        return;
                    }

                    setSession((current) => {
                        // Apply the synced envelope only if nothing changed
                        // while the save was in flight — a newer local edit
                        // outranks the acknowledgement of an older one.
                        if (current.uid !== uid
                            || !current.envelope
                            || current.envelope.syncMeta.revision !== result.envelope.syncMeta.revision) {
                            return current;
                        }

                        writeAccountEnvelope(uid, result.envelope);
                        return { uid, plantIds: result.envelope.garden.plantIds, envelope: result.envelope };
                    });
                })
                .catch((error) => {
                    if (cancelled) {
                        return;
                    }

                    if (error instanceof GardenSyncStaleWriteError) {
                        setSession((current) => {
                            if (current.uid !== uid || !current.envelope) {
                                return current;
                            }

                            const now = new Date().toISOString();
                            const resolved = resolveGardenMerge(current.envelope, error.remoteEnvelope, { deviceId, now });

                            writeAccountEnvelope(uid, resolved);
                            return { uid, plantIds: resolved.garden.plantIds, envelope: resolved };
                        });
                        return;
                    }

                    // Offline or a transient failure: the envelope stays
                    // dirty, so retrying is just running this effect again.
                    console.error(error);
                    retryTimer = setTimeout(() => setSaveTick((tick) => tick + 1), SAVE_RETRY_DELAY_MS);
                }))
            .finally(() => {
                isSavingRef.current = false;
            });

        return () => {
            cancelled = true;

            if (retryTimer) {
                clearTimeout(retryTimer);
            }
        };
    }, [uid, session, hasHydratedRemote, saveTick, deviceId]);

    // Focus, reconnect and visibility force a fresh remote read — an installed
    // app that spent the day in a pocket should notice the other device's
    // garden when it is opened, not a minute later (the same reasoning as
    // SPEC-010's update checks).
    useEffect(() => {
        if (!uid || typeof window === 'undefined') {
            return undefined;
        }

        const refresh = () => setRemoteTick((tick) => tick + 1);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refresh();
            }
        };

        window.addEventListener('focus', refresh);
        window.addEventListener('online', refresh);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('focus', refresh);
            window.removeEventListener('online', refresh);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [uid]);

    const applyPlantIds = useCallback((nextPlantIds) => {
        setSession((current) => {
            if (current.uid) {
                const now = new Date().toISOString();
                const nextEnvelope = createNextGardenEnvelope(current.envelope, {
                    plantIds: nextPlantIds,
                    deviceId,
                    now,
                });

                writeAccountEnvelope(current.uid, nextEnvelope);
                return { uid: current.uid, plantIds: nextEnvelope.garden.plantIds, envelope: nextEnvelope };
            }

            // Written before the state change, so a reload that beats the next
            // paint still agrees with what the user just tapped.
            writeGuestGarden(nextPlantIds);
            return { ...current, plantIds: nextPlantIds };
        });
    }, [deviceId]);

    const toggle = useCallback((plantId) => {
        applyPlantIds(
            session.plantIds.includes(plantId)
                ? session.plantIds.filter((id) => id !== plantId)
                : [...session.plantIds, plantId],
        );
    }, [applyPlantIds, session.plantIds]);

    /**
     * Put a plant back where it was (SPEC-014 D-4).
     *
     * `toggle` would append it, which on the My Garden screen — ordered newest
     * first — means an undo moves the card to the top of the collection instead
     * of back into the gap it just left. Undo should leave no trace, including
     * in the order.
     */
    const restore = useCallback((plantId, index) => {
        if (session.plantIds.includes(plantId)) {
            return;
        }

        const next = [...session.plantIds];
        next.splice(index, 0, plantId);

        applyPlantIds(next);
    }, [applyPlantIds, session.plantIds]);

    const value = useMemo(() => ({
        plantIds: session.plantIds,
        isInGarden: (plantId) => session.plantIds.includes(plantId),
        toggle,
        restore,
        // What the garden screen's note states: a guest garden is this
        // device's alone (SPEC-014 D-5); an account garden follows the account.
        isCloudSynced: Boolean(session.uid),
    }), [session.plantIds, session.uid, toggle, restore]);

    return <GardenContext.Provider value={value}>{children}</GardenContext.Provider>;
}

export function useGarden() {
    const value = useContext(GardenContext);

    if (!value) {
        throw new Error('useGarden must be used inside a GardenProvider');
    }

    return value;
}
